<template>
  <v-form ref="formRef" class="lighting-shared-form method-form" @submit.prevent="">
    <div class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">mdi-office-building-outline</v-icon>
        <span>Gebäudedaten</span>
      </div>

      <!-- Building Type -->
      <v-autocomplete
        v-model="form.buildingType"
        :error-messages="validationErrors?.building_type"
        label="Gebäudetyp"
        :items="buildingTypes"
        item-title="name"
        item-value="id"
        validate-on-blur
        class="mb-3"
        @update:model-value="applyAreaDefaults"
      >
        <template #append>
          <v-tooltip v-if="hint.building_type" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">mdi-information</v-icon>
            </template>
            <span>{{ hint.building_type }}</span>
          </v-tooltip>
        </template>
      </v-autocomplete>

      <!-- BGF -->
      <v-text-field
        v-model.number="form.bgf"
        type="number"
        label="Bruttogrundfläche"
        suffix="m²"
        :error-messages="validationErrors?.bgf"
        :disabled="!form.buildingType"
        :hint="hint.bgf"
        validate-on-blur
        class="mb-3 ml-8"
      />

      <!-- Anteil Hauptfläche -->
      <v-text-field
        v-model.number="form.partMainArea"
        type="number"
        label="Anteil Hauptfläche"
        suffix="%"
        :disabled="!form.bgf"
        :error-messages="validationErrors?.part_main_area"
        :hint="hint.part_main_area"
        validate-on-blur
        class="mb-3 ml-8"
      />

      <!-- Anteil Nebenfläche -->
      <v-text-field
        v-model.number="form.partAuxArea"
        type="number"
        label="Anteil Nebenfläche"
        suffix="%"
        :disabled="!form.bgf"
        :error-messages="validationErrors?.part_aux_area"
        :hint="hint.part_aux_area"
        validate-on-blur
        class="mb-3 ml-8"
      />
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import api from '@/plugins/axios'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props expected by parent (action.vue) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})

const emit = defineEmits(['update:modelValue'])

/* Field mapping — backend keys */
const fields = {
  buildingType:   'building_type',
  bgf:            'bgf',
  partMainArea:   'part_main_area',
  partAuxArea:    'part_aux_area',
}

/* useBaseForm — as SHARED FORM */
const {
  form,
  hint,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {},
  reference: computed(() => props.reference),
  planned:   computed(() => props.planned),
  shared:    computed(() => true),   // THIS IS A SHARED FORM
  otherData: computed(() => props.otherData),
  sharedData:computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (e, p) => emit(e, p),
})

/* Local data */
const buildingTypes = ref([])

/* API: fetch building type choices (updated endpoint) */
async function fetchBuildingTypes() {
  try {
    const { data } = await api.get('/methods/lighting1/building_type/', {
      params: {
        planned: props.planned || null,
        reference: props.reference || null,
      },
    })
    buildingTypes.value = data
  } catch {}
}

/* Prefill the area split from the chosen building type (WP-07 / finding V4).
 *
 * The 2014 Excel prescribed the main/auxiliary split per building type; the port dropped it and
 * left the fields empty — that is how storage and industry ended up with a non-zero auxiliary
 * share and a lux factor above 1 (finding B5).
 *
 * Bound to `@update:model-value`, not to a watcher on `form.buildingType`: the event fires only
 * on a user pick, so parent ingestion cannot overwrite the split stored with an existing record.
 * The values are a suggestion — both fields stay editable.
 */
function applyAreaDefaults(buildingTypeId) {
  const chosen = buildingTypes.value.find(bt => bt.id === buildingTypeId)
  if (!chosen) return
  if (chosen.default_part_main == null || chosen.default_part_aux == null) return

  form.partMainArea = chosen.default_part_main
  form.partAuxArea = chosen.default_part_aux
}

/* expose validateForm() and backend fields for parent shared validation */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})

/* lifecycle */
onMounted(async () => {
  await fetchBuildingTypes()
  await fetchHints()
})
</script>

<style scoped lang="scss">
.lighting-shared-form {
  width: 100%;
}

/* Field styling */
.lighting-shared-form :deep(.v-field) {
  border-radius: 18px;
}

.lighting-shared-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Section wrapper */
.method-section {
  margin-bottom: 0;
  padding: 1rem;
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
}

/* Section heading */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 18px;
  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);
  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  font-size: 1rem;
  font-weight: 700;
}

/* Nested/dependent fields */
.lighting-shared-form :deep(.ml-8) {
  margin-left: 1.5rem !important;
  padding-left: 1rem;
  border-left: 2px solid rgba(var(--v-theme-primary), 0.12);
}

/* Hint icons */
.lighting-shared-form :deep(.v-input__append),
.lighting-shared-form :deep(.v-field__append-inner) {
  align-items: center;
}

.lighting-shared-form :deep(.v-input__append .v-icon),
.lighting-shared-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

/* Disabled fields */
.lighting-shared-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .lighting-shared-form :deep(.ml-8) {
    margin-left: 0.75rem !important;
    padding-left: 0.75rem;
  }
}
</style>
